import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useSnackbar } from 'notistack'
import './ParentNameModal.css'

const MAX_NAME_LENGTH = 100

// Blocking, post-signup modal (Phase 1). Shown only when the signed-in
// parent's profile has pendingParentName: true — set exclusively by
// provisionParentProfile for brand-new accounts, so legacy parents with
// empty names never see this. There is deliberately no Skip/Close affordance:
// the only way out is a successful submit.
const ParentNameModal = ({ onComplete }) => {
  const { completeParentName } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const trimmedFirstName = firstName.trim()
  const trimmedLastName = lastName.trim()
  const canSubmit = trimmedFirstName.length > 0 && trimmedLastName.length > 0 && !submitting

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!trimmedFirstName || !trimmedLastName) {
      enqueueSnackbar('Please enter your first and last name', { variant: 'error' })
      return
    }
    if (trimmedFirstName.length > MAX_NAME_LENGTH || trimmedLastName.length > MAX_NAME_LENGTH) {
      enqueueSnackbar('Names must be shorter than 100 characters', { variant: 'error' })
      return
    }

    setSubmitting(true)
    try {
      await completeParentName(trimmedFirstName, trimmedLastName)
      if (isMountedRef.current) {
        enqueueSnackbar('Thanks! Your account is all set.', { variant: 'success' })
      }
      await onComplete()
    } catch (error) {
      if (isMountedRef.current) {
        enqueueSnackbar(error.message || 'Error saving your name. Please try again.', { variant: 'error' })
      }
    }
    if (isMountedRef.current) {
      setSubmitting(false)
    }
  }

  return (
    <div className='parent-name-modal-overlay'>
      <div className='parent-name-modal' role='dialog' aria-modal='true' aria-labelledby='parent-name-modal-title'>
        <div className='parent-name-modal-emoji'>👋</div>
        <h2 id='parent-name-modal-title' className='parent-name-modal-title'>What's your name?</h2>
        <p className='parent-name-modal-subtitle'>
          We use this to personalize your Quillnote parent dashboard.
        </p>

        <form className='parent-name-modal-form' onSubmit={handleSubmit}>
          <div className='parent-name-modal-fields-row'>
            <input
              type='text'
              placeholder='First Name'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className='parent-name-modal-input'
              disabled={submitting}
              maxLength={MAX_NAME_LENGTH}
              autoFocus
              required
            />
            <input
              type='text'
              placeholder='Last Name'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className='parent-name-modal-input'
              disabled={submitting}
              maxLength={MAX_NAME_LENGTH}
              required
            />
          </div>

          <button
            type='submit'
            className='parent-name-modal-submit'
            disabled={!canSubmit}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ParentNameModal
